import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shrimp, ClipboardText, Scales, CurrencyDollar, TrendUp, TrendDown, Warning, CalendarBlank, Bell, Package, ArrowRight, ArrowsClockwise } from "@phosphor-icons/react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const FIELD_ROLES = ["asisten_supervisor", "korsub", "korwil"];

const PERIOD_OPTIONS = [
  { value: "7d", label: "7 Hari" },
  { value: "30d", label: "30 Hari" },
  { value: "90d", label: "3 Bulan" },
  { value: "180d", label: "6 Bulan" },
  { value: "365d", label: "1 Tahun" },
  { value: "all", label: "Semua" },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const isFieldRole = FIELD_ROLES.includes(user?.role);
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [earlyWarnings, setEarlyWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, n, ew] = await Promise.all([
        api.get(`/dashboard/stats?period=${period}`),
        api.get(`/dashboard/charts?period=${period}`),
        api.get("/notifications?limit=5").catch(() => ({ data: { data: [] } })),
        api.get("/early-warning").catch(() => ({ data: { data: [] } }))
      ]);
      setStats(s.data);
      setCharts(c.data);
      setNotifications(n.data.data || []);
      setEarlyWarnings(ew.data.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(val || 0);

  const formatCurrencyShort = (val) => {
    if (val >= 1000000000) return `${(val / 1000000000).toFixed(1)}M`;
    if (val >= 1000000) return `${(val / 1000000).toFixed(0)}jt`;
    if (val >= 1000) return `${(val / 1000).toFixed(0)}rb`;
    return String(val);
  };

  if (loading && !stats) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 sm:h-28 bg-white rounded-lg border border-slate-200 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // ---- FIELD ROLE: Simplified Dashboard ----
  if (isFieldRole) {
    return (
      <div data-testid="dashboard-page" className="space-y-6">
        <div className="text-center py-4">
          <img src="/logo-p3uw.png" alt="P3UW" className="w-16 h-16 rounded-xl mx-auto mb-3 shadow-lg" />
          <h1 className="text-xl sm:text-2xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Selamat Datang, {user?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Role: {user?.role === "asisten_supervisor" ? "Asisten Supervisor" : user?.role === "korsub" ? "KORSUB" : "KORWIL"}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto">
          <Card
            className="bg-white border-2 border-[#008080]/20 hover:border-[#008080] hover:shadow-lg cursor-pointer transition-all duration-200"
            onClick={() => navigate("/tambak")}
            data-testid="field-nav-tambak"
          >
            <CardContent className="p-6 text-center">
              <Shrimp size={36} weight="duotone" className="text-[#008080] mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Manajemen Tambak</p>
              <p className="text-xs text-slate-500 mt-1">Lihat data tambak</p>
            </CardContent>
          </Card>
          <Card
            className="bg-white border-2 border-[#B8860B]/20 hover:border-[#B8860B] hover:shadow-lg cursor-pointer transition-all duration-200"
            onClick={() => navigate("/siklus")}
            data-testid="field-nav-siklus"
          >
            <CardContent className="p-6 text-center">
              <ArrowsClockwise size={36} weight="duotone" className="text-[#B8860B] mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Siklus & Monitoring</p>
              <p className="text-xs text-slate-500 mt-1">Isi monitoring harian</p>
            </CardContent>
          </Card>
        </div>

        {stats && (
          <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
            <Card className="bg-white border border-slate-200"><CardContent className="p-4 text-center">
              <p className="text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>{stats.total_tambak}</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Tambak</p>
            </CardContent></Card>
            <Card className="bg-white border border-slate-200"><CardContent className="p-4 text-center">
              <p className="text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>{stats.total_siklus_aktif}</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Siklus Aktif</p>
            </CardContent></Card>
          </div>
        )}
      </div>
    );
  }

  // ---- KOPERASI ROLE: Full Dashboard ----
  const kpiCards = [
    { title: "Total Tambak", value: stats?.total_tambak || 0, icon: Shrimp, color: "bg-[#B8860B]", subtitle: `${stats?.total_siklus_aktif || 0} siklus aktif` },
    { title: "Laporan Produksi", value: stats?.total_laporan || 0, icon: ClipboardText, color: "bg-[#008080]", subtitle: "Total laporan" },
    { title: "Total Panen", value: stats?.total_panen || 0, icon: Scales, color: "bg-amber-500", subtitle: "Record panen" },
    { title: "Saldo", value: formatCurrency(stats?.saldo), icon: CurrencyDollar, color: stats?.saldo >= 0 ? "bg-[#008080]" : "bg-red-500", subtitle: `In: ${formatCurrency(stats?.total_pemasukan)}`, isText: true },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-4 sm:space-y-6">
      {/* Header + Period filter */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Dashboard
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Ringkasan operasional koperasi P3UW</p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarBlank size={16} className="text-slate-400 hidden sm:block" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="period-filter" className="w-[140px] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards - 2 cols mobile, 4 cols desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        {kpiCards.map((kpi, i) => (
          <Card key={i} data-testid={`kpi-card-${i}`} className="bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200">
            <CardContent className="p-3 sm:p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs uppercase tracking-[0.08em] font-semibold text-slate-500 mb-1 sm:mb-2 truncate">{kpi.title}</p>
                  <p className={`font-semibold text-[#1A1A1A] ${kpi.isText ? "text-sm sm:text-lg" : "text-lg sm:text-2xl"} truncate`} style={{ fontFamily: "Work Sans" }}>{kpi.value}</p>
                  <p className="text-[10px] sm:text-xs text-slate-400 mt-0.5 truncate">{kpi.subtitle}</p>
                </div>
                <div className={`${kpi.color} p-1.5 sm:p-2.5 rounded-lg shrink-0 ml-2`}>
                  <kpi.icon size={18} weight="duotone" className="text-white sm:w-[22px] sm:h-[22px]" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Early Warning Alerts */}
      {earlyWarnings.length > 0 && (
        <Card className="bg-red-50 border border-red-200" data-testid="early-warning-widget">
          <CardHeader className="pb-2 px-4 sm:px-6 pt-4">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base font-semibold text-red-800" style={{ fontFamily: "Work Sans" }}>
              <Warning size={18} weight="bold" className="text-red-600" /> Peringatan Dini ({earlyWarnings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4">
            <div className="space-y-2">
              {earlyWarnings.slice(0, 5).map((a, i) => (
                <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg border ${a.severity === "bahaya" ? "bg-red-100 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-800">{a.tambak_nama} — {a.parameter}: <span className={a.severity === "bahaya" ? "text-red-700" : "text-amber-700"}>{a.nilai}</span></p>
                    <p className="text-[10px] text-slate-500">Normal: {a.batas_normal} | {a.tanggal}</p>
                  </div>
                  <Badge className={`text-[10px] shrink-0 ml-2 ${a.severity === "bahaya" ? "bg-red-600 text-white hover:bg-red-600" : "bg-amber-500 text-white hover:bg-amber-500"}`}>
                    {a.severity === "bahaya" ? "BAHAYA" : "PERINGATAN"}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Financial Summary + Recent Reports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="text-sm sm:text-base font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Ringkasan Keuangan</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg bg-emerald-50 border border-emerald-100">
              <div className="flex items-center gap-2 sm:gap-3">
                <TrendUp size={18} className="text-emerald-600 shrink-0" weight="bold" />
                <span className="text-xs sm:text-sm font-medium text-emerald-800">Pemasukan</span>
              </div>
              <span className="text-xs sm:text-sm font-semibold text-emerald-700">{formatCurrency(stats?.total_pemasukan)}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg bg-red-50 border border-red-100">
              <div className="flex items-center gap-2 sm:gap-3">
                <TrendDown size={18} className="text-red-600 shrink-0" weight="bold" />
                <span className="text-xs sm:text-sm font-medium text-red-800">Pengeluaran</span>
              </div>
              <span className="text-xs sm:text-sm font-semibold text-red-700">{formatCurrency(stats?.total_pengeluaran)}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg bg-[#B8860B]/5 border border-[#B8860B]/10">
              <span className="text-xs sm:text-sm font-medium text-[#B8860B]">Saldo</span>
              <span className="text-base sm:text-lg font-bold text-[#B8860B]">{formatCurrency(stats?.saldo)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="text-sm sm:text-base font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Laporan Terbaru</CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {stats?.recent_laporan?.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {stats.recent_laporan.map((r) => (
                  <div key={r._id} className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs sm:text-sm font-medium text-slate-800 truncate">{r.tambak_nama || "Tambak"}</p>
                      <p className="text-[10px] sm:text-xs text-slate-400">DOC {r.hari_ke} &middot; {r.tanggal}</p>
                    </div>
                    <Badge variant={r.status === "verified" ? "default" : "secondary"} className={`text-[10px] sm:text-xs shrink-0 ml-2 ${r.status === "verified" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}`}>
                      {r.status === "verified" ? "Verified" : "Pending"}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-slate-400 text-center py-6 sm:py-8">Belum ada laporan</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notifications Widget + Low Stock row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Notifications widget */}
        <Card className="bg-white border border-slate-200" data-testid="dashboard-notifications-widget">
          <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>
                <Bell size={18} weight="duotone" className="text-[#B8860B]" /> Notifikasi Terbaru
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/audit-log")} className="text-xs text-[#B8860B] h-7 px-2">
                Lihat Semua <ArrowRight size={12} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {notifications.length > 0 ? (
              <div className="space-y-2">
                {notifications.map((n) => {
                  const isStock = n.tipe === "stok_rendah";
                  return (
                    <div key={n._id} className={`flex items-start gap-2.5 p-2.5 rounded-lg border transition-colors ${!n.is_read ? "bg-[#B8860B]/[0.03] border-[#B8860B]/10" : "border-slate-100 hover:bg-slate-50"}`}>
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${isStock ? "bg-amber-50" : "bg-blue-50"}`}>
                        {isStock ? <Package size={14} className="text-amber-500" weight="duotone" /> : <ClipboardText size={14} className="text-blue-500" weight="duotone" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs leading-snug truncate ${!n.is_read ? "font-semibold text-[#1A1A1A]" : "text-slate-600"}`}>{n.judul}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 truncate">{n.pesan}</p>
                      </div>
                      {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-[#B8860B] shrink-0 mt-1.5" />}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-6">Tidak ada notifikasi baru</p>
            )}
          </CardContent>
        </Card>

        {/* Low stock */}
        <Card className={`border ${stats?.low_stock_items?.length > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
          <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base font-semibold" style={{ fontFamily: "Work Sans", color: stats?.low_stock_items?.length > 0 ? "#92400E" : "#1A1A1A" }}>
              <Warning size={18} weight="bold" className={stats?.low_stock_items?.length > 0 ? "text-amber-600" : "text-slate-400"} />
              Peringatan Stok
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {stats?.low_stock_items?.length > 0 ? (
              <div className="space-y-2">
                {stats.low_stock_items.map((item) => (
                  <div key={item._id} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-amber-100">
                    <span className="text-xs sm:text-sm font-medium text-slate-700 truncate">{item.nama}</span>
                    <span className="text-xs sm:text-sm font-semibold text-amber-600 shrink-0 ml-2">{item.stok} {item.satuan}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-6">Semua stok gudang mencukupi</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-1 sm:pb-2"><CardTitle className="text-sm sm:text-base font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Tren Keuangan Bulanan</CardTitle></CardHeader>
          <CardContent className="px-2 sm:px-6 pb-4">
            {charts?.financial_chart?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={charts.financial_chart} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="bulan" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={formatCurrencyShort} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="pemasukan" name="Pemasukan" fill="#008080" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="pengeluaran" name="Pengeluaran" fill="#DC2626" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 text-center py-10">Belum ada data</p>}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-1 sm:pb-2"><CardTitle className="text-sm sm:text-base font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Tren Panen Bulanan</CardTitle></CardHeader>
          <CardContent className="px-2 sm:px-6 pb-4">
            {charts?.harvest_chart?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={charts.harvest_chart} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="bulan" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                  <Tooltip formatter={(v, name) => name === "total_kg" ? `${v} kg` : formatCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total_kg" name="Total Kg" fill="#B8860B" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 text-center py-10">Belum ada data</p>}
          </CardContent>
        </Card>
      </div>

      {/* Production Line Chart */}
      {charts?.production_chart?.length > 0 && (
        <Card className="bg-white border border-slate-200">
          <CardHeader className="pb-1 sm:pb-2"><CardTitle className="text-sm sm:text-base font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Monitoring Produksi Terakhir</CardTitle></CardHeader>
          <CardContent className="px-2 sm:px-6 pb-4">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={charts.production_chart} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="tanggal" tick={{ fontSize: 9, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="pakan_total" name="Pakan (kg)" stroke="#B8860B" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="ph" name="pH" stroke="#008080" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="suhu" name="Suhu" stroke="#F59E0B" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
